import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Select from '@/components/Select/Select'
import type { FormFieldType } from '@/types/form-field-type'

import type { CurriculumForm } from '../hooks/useCurriculumForm'
import { useAllCoursesOptions } from '../queries/useAllCoursesOptions'
import useInputsRegistry from '../store/useInputsRegistry'

const BasketCourseSelect = (props: FormFieldType<string>) => {
  const { t } = useTranslation('curriculumsManagement')

  const form = useFormContext<CurriculumForm>()
  const educationLevelId = form.watch('educationLevel')

  const { data, isLoading } = useAllCoursesOptions({ educationLevelId })
  const { registry, setRegistryValue, destroyInput } = useInputsRegistry()

  const selectedCourses = useMemo(() => {
    const { [props.id]: _, ...rest } = registry

    return Object.fromEntries(
      Object.entries(rest).map(([, value]) => [value, true])
    )
  }, [registry, props.id])

  const options = useMemo(
    () => (data ?? []).filter(option => !selectedCourses?.[option.value]),
    [data, selectedCourses]
  )

  useEffect(() => {
    if (props.value) {
      setRegistryValue(props.id, props.value)
    }

    return () => {
      destroyInput(props.id)
    }
  }, [props.id, props.value, setRegistryValue, destroyInput])

  return (
    <Select
      {...props}
      placeholder={t('placeholder.select-course')}
      loading={isLoading}
      options={options}
    />
  )
}

export default BasketCourseSelect
